import React, { useEffect } from 'react';
import {
    FormControl,
    FormHelperText,
    InputLabel,
    MenuItem,
    Select,
    Stack,
} from '@mui/material';
import isArray from 'lodash/isArray';
import { FormFieldProps } from './index';

import { useTranslation } from '../../../contexts/TranslationContext';
import { useFormBlocks, useFormErrors } from '../../../contexts/FormContext';
import FileUploadFormField from './file-upload';
import { getIds } from '../../../utils/form';

function DefaultSelectionFormField({
    name,
    label,
    required,
    disabled,
    value,
    choices,
    multiple,
    onChange,
    cssClassName,
    attr,
}: FormFieldProps) {
    const { translate } = useTranslation();
    const { error } = useFormErrors(name);
    const { showBlocks } = useFormBlocks();

    const objectArr =
        isArray(value) &&
        value?.filter((item: any) => typeof item === 'object')?.length > 0;
    const filteredValue = objectArr ? getIds(value) : value;

    const runShowLogic = () => {
        if (attr?.['show-logic']) {
            const noValueMode = Boolean(attr['show-logic-no-value']) ?? false;
            showBlocks(noValueMode ? attr['show-logic'][Boolean(filteredValue).toString()] : attr['show-logic'][filteredValue] ?? []);
        }
    };

    useEffect(runShowLogic, [attr, showBlocks, filteredValue]);

    const renderOption = (optionKey: string, optionIndex: number) => {
        const option = choices?.[optionKey];
        let selected = false;
        if (isArray(value)) {
            selected = filteredValue?.includes(option);
        } else {
            selected = (value?.id ?? value) === option;
        }
        return (
            <MenuItem key={optionIndex} value={option} selected={selected}>
                {translate(typeof optionKey !== 'undefined' ? optionKey : '')}
            </MenuItem>
        );
    };

    const onValueChange = (newValue: any) => {
        runShowLogic();
        onChange(newValue);
    };

    return (
        <FormControl fullWidth className={cssClassName}>
            <Stack spacing={0.5}>
                <InputLabel
                    sx={{ lineHeight: 1, color: 'text.secondary', mt: 0.5 }}
                    id={`form-field-label-${name}`}>
                    {translate(label)}
                </InputLabel>
                {choices ? (
                    <Select
                        labelId={`form-field-label-${name}`}
                        name={name}
                        required={required}
                        disabled={disabled}
                        onChange={event => onValueChange(event.target.value)}
                        multiple={multiple ?? false}
                        error={Boolean(error)}
                        value={
                            filteredValue ? filteredValue : multiple ? [] : ''
                        }
                        label={translate(label)}>
                        {Object.keys(choices).map(renderOption)}
                    </Select>
                ) : (
                    <Select
                        labelId={'form-field-label-select'}
                        id={'empty-select'}
                        name={'empty-select'}
                        required={required}
                        disabled={disabled}
                        error={Boolean(error)}
                        value={filteredValue}
                        label={translate('form.general.empty_select')}>
                        <MenuItem key={'empty'} value={''} selected>
                            {translate('form.general.empty_select')}
                        </MenuItem>
                    </Select>
                )}
                {Boolean(error) && (
                    <FormHelperText error id={name + '-error'}>
                        {translate(error ?? 'error')}
                    </FormHelperText>
                )}
            </Stack>
        </FormControl>
    );
}

export default function SelectionFormField(props: FormFieldProps) {
    const { attr } = props;

    if (attr && attr['data-upload']) {
        return <FileUploadFormField {...props} />;
    }
    return <DefaultSelectionFormField {...props} />;
}
